import { requiredCoverWidth } from '@/lib/cdn/coverTransform';

/** Matches `styles/tokens/_breakpoints.scss` `$breakpoint-md` and MediaWrapper. */
const MOBILE_MAX_WIDTH = 767;

export type UniversalCardImageSizesInput = {
  cardType?: string | null;
  textMaxWidth?: string | null;
  columnDesktop?: number | null;
  columnMobile?: number | null;
  mediaHeightDesktop?: number | null;
  mediaHeightMobile?: number | null;
  /** Source width / height at desktop breakpoint. */
  ratioDesktop?: number | null;
  /** Source width / height at mobile breakpoint. */
  ratioMobile?: number | null;
};

export type UniversalCardCoverSlot = {
  mobile?: { height: number; sourceAspectRatio: number };
  desktop?: { height: number; sourceAspectRatio: number };
};

function hasFixedMediaHeight(input: UniversalCardImageSizesInput): boolean {
  return input.mediaHeightDesktop != null || input.mediaHeightMobile != null;
}

function isNaturalAspectStackedMedia(cardType: string | null | undefined): boolean {
  return cardType === 'MediaTop' || cardType === 'MediaBottom';
}

function needsCoverAwareImageSizing(
  cardType: string | null | undefined,
  fixedHeight: boolean
): boolean {
  if (!fixedHeight) return false;
  if (cardType === 'MediaBackground') return true;
  if (cardType === 'MediaLeft' || cardType === 'MediaRight') return true;
  return isNaturalAspectStackedMedia(cardType);
}

function desktopDenominator(
  cardType: string | null | undefined,
  textMaxWidth: string | null | undefined,
  colsDesktop: number
): number {
  const isSideBySide = cardType === 'MediaLeft' || cardType === 'MediaRight';
  if (!isSideBySide) return colsDesktop;

  if (textMaxWidth?.toLowerCase() === 'large') {
    return colsDesktop * 3;
  }
  return colsDesktop * 2;
}

function coverAwareSizeExpr(
  slotWidthExpr: string,
  slotHeight: number,
  sourceAspectRatio: number
): string {
  const minWidth = requiredCoverWidth(0, slotHeight, sourceAspectRatio);
  return `max(${slotWidthExpr}, ${minWidth}px)`;
}

/**
 * Build a responsive `sizes` hint for Universal Card media from grid column
 * count and card layout. Side-by-side types only split media on desktop; mobile
 * keeps full card width.
 *
 * Natural-aspect MediaTop/MediaBottom cards keep layout width only. Fixed-height
 * and other cover-fill slots inflate the hint to `max(slotWidth, height * ratio)`
 * so landscape sources stay sharp in tall panes.
 *
 * Example Input:
 * { cardType: 'MediaTop', columnDesktop: 4, columnMobile: 1 }
 *
 * Example Output:
 * "(max-width: 767px) calc(100vw / 1), calc(100vw / 4)"
 */
export function buildUniversalCardImageSizes(input: UniversalCardImageSizesInput): string {
  const colsMobile = Math.max(input.columnMobile ?? 1, 0.5);
  const colsDesktop = Math.max(input.columnDesktop ?? 1, 0.5);
  const mobileSize = `calc(100vw / ${colsMobile})`;
  const desktopSize = `calc(100vw / ${desktopDenominator(input.cardType, input.textMaxWidth, colsDesktop)})`;

  const fixedHeight = hasFixedMediaHeight(input);
  if (isNaturalAspectStackedMedia(input.cardType) && !fixedHeight) {
    return `(max-width: ${MOBILE_MAX_WIDTH}px) ${mobileSize}, ${desktopSize}`;
  }

  if (!needsCoverAwareImageSizing(input.cardType, fixedHeight)) {
    return `(max-width: ${MOBILE_MAX_WIDTH}px) ${mobileSize}, ${desktopSize}`;
  }

  let mobile = mobileSize;
  let desktop = desktopSize;

  if (input.mediaHeightMobile != null && input.ratioMobile != null) {
    mobile = coverAwareSizeExpr(mobileSize, input.mediaHeightMobile, input.ratioMobile);
  }
  if (input.mediaHeightDesktop != null && input.ratioDesktop != null) {
    desktop = coverAwareSizeExpr(desktopSize, input.mediaHeightDesktop, input.ratioDesktop);
  }

  return `(max-width: ${MOBILE_MAX_WIDTH}px) ${mobile}, ${desktop}`;
}

/**
 * Cover slot dimensions for MediaWrapper's cover-aware loader.
 *
 * Example Input:
 * {
 *   cardType: 'MediaLeft',
 *   mediaHeightDesktop: 720,
 *   ratioDesktop: 1.65,
 * }
 *
 * Example Output:
 * { desktop: { height: 720, sourceAspectRatio: 1.65 } }
 */
export function buildUniversalCardCoverSlot(
  input: UniversalCardImageSizesInput
): UniversalCardCoverSlot | undefined {
  if (!needsCoverAwareImageSizing(input.cardType, hasFixedMediaHeight(input))) {
    return undefined;
  }

  const mobile =
    input.mediaHeightMobile != null && input.ratioMobile != null
      ? { height: input.mediaHeightMobile, sourceAspectRatio: input.ratioMobile }
      : undefined;
  const desktop =
    input.mediaHeightDesktop != null && input.ratioDesktop != null
      ? { height: input.mediaHeightDesktop, sourceAspectRatio: input.ratioDesktop }
      : undefined;

  if (!mobile && !desktop) return undefined;
  return { mobile, desktop };
}
